import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

/**
 * ArticleContent Component - Production Quality
 * Renders article content as proper HTML with XSS sanitization
 * 
 * Post-processes HTML to enhance:
 * - Adds id anchors to H2/H3 for TOC linking
 * - Wraps "Quick Picks" style intro text in a summary box
 * - Adds section spacing between major sections
 */
export const ArticleContent = ({ content }) => {
  const sanitizedContent = useMemo(() => {
    if (!content) return '';
    
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(content);
    
    if (!hasHtmlTags) {
      const paragraphs = content.split(/\n\n+/).filter(p => p.trim());
      if (paragraphs.length === 0) return '';
      return paragraphs.map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
    }
    
    let cleanHtml = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr', 'span', 'div',
        'ul', 'ol', 'li',
        'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins',
        'sub', 'sup', 'mark',
        'a', 'img',
        'blockquote', 'code', 'pre',
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col',
        'figure', 'figcaption',
        'address', 'article', 'aside', 'footer', 'header', 'main', 'nav', 'section'
      ],
      ALLOWED_ATTR: [
        'href', 'target', 'rel', 'title',
        'src', 'alt', 'width', 'height', 'loading',
        'class', 'id', 'style',
        'data-*',
        'colspan', 'rowspan', 'scope'
      ],
      ALLOW_DATA_ATTR: true,
      ADD_ATTR: ['target'],
      ALLOW_STYLE_ATTR: true,
    });

    // Post-process: Add anchor IDs to headings for TOC
    let headingIndex = 0;
    cleanHtml = cleanHtml.replace(/<h([2-3])([^>]*)>([\s\S]*?)<\/h\1>/gi, (match, level, attrs, text) => {
      const plainText = text.replace(/<[^>]+>/g, '').trim();
      const id = `section-${plainText.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50)}`;
      headingIndex++;
      return `<h${level}${attrs} id="${id}">${text}</h${level}>`;
    });

    // Post-process: Strip "Quick Picks" summary block before first H2
    // This removes redundant "Best Overall: X / Best Camera: Y" lists
    const firstH2 = cleanHtml.indexOf('<h2');
    if (firstH2 > 0) {
      const beforeH2 = cleanHtml.substring(0, firstH2);
      const bestPattern = /Best\s+\w+:/gi;
      const matches = beforeH2.match(bestPattern);
      if (matches && matches.length >= 3) {
        // Remove all <p> tags before first H2 that contain "Best X:" lines
        const stripped = beforeH2.replace(/<p>([\s\S]*?)<\/p>/gi, (pMatch, inner) => {
          const text = inner.replace(/<[^>]+>/g, '');
          if (/Best\s+\w+:/i.test(text)) return '';
          return pMatch;
        });
        cleanHtml = stripped + cleanHtml.substring(firstH2);
      }
    }
    
    return cleanHtml;
  }, [content]);

  if (!sanitizedContent) {
    return (
      <div className="py-12 text-center text-gray-400">
        <p>No content available for this article.</p>
      </div>
    );
  }
  
  return (
    <div 
      className="article-content"
      data-testid="article-content"
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

export default ArticleContent;
